/**
 * Client-side validation, mirroring the API's rules so people are told what
 * is wrong while typing rather than after a round trip. The server validates
 * again regardless — this is for the person, not for safety.
 */

const NAME_RE = /^[A-Za-z0-9_]+(?: [A-Za-z0-9_]+)?$/;
const PHONE_RE = /^\+?[0-9][0-9\s\-()]{7,19}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function validateName(value: string): string | null {
  const name = value.trim();
  if (!name) return "Enter your name";
  if (name.length < 3 || name.length > 20) return "Name must be 3–20 characters";
  if (/^\d/.test(name)) return "Name can't start with a number";
  if (!NAME_RE.test(name))
    return "Letters, numbers and underscores, with at most one space between two words";
  return null;
}

export function validateEmail(value: string): string | null {
  if (!value.trim()) return "Enter your email address";
  if (!EMAIL_RE.test(value.trim())) return "Enter a valid email address";
  return null;
}

export function validatePhone(value: string): string | null {
  const phone = value.trim();
  if (!phone) return "Enter your WhatsApp number";
  if (!PHONE_RE.test(phone)) return "Include your country code, e.g. +91 98765 43210";
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 10 || digits.length > 15)
    return "A WhatsApp number is 10–15 digits including the country code";
  return null;
}

export function validatePassword(value: string): string | null {
  if (!value) return "Choose a password";
  if (value.length < 8) return "At least 8 characters";
  if (!/[A-Za-z]/.test(value) || !/[0-9]/.test(value))
    return "Include at least one letter and one number";
  return null;
}

export function formatDuration(seconds: number | null | undefined): string {
  if (!seconds && seconds !== 0) return "—";
  const total = Math.round(seconds);
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return `${minutes}:${String(rest).padStart(2, "0")}`;
}

export function formatDate(iso?: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}
